/* ==========================================================================
   モーダル (.js-modal--trigger / .js-modal--content)
   ========================================================================== */
(() => {
  const TRIGGER_SELECTOR = '.js-modal--trigger';
  const MODAL_SELECTOR = '.js-modal--content';
  const CLOSE_SELECTOR = '[data-modal-close]';
  const CLOSE_FALLBACK_MS = 400; // CSS の transition-duration(0.25s)より余裕を持たせた保険

  const openModal = (modal) => {
    if (modal instanceof HTMLDialogElement && !modal.open) {
      modal.showModal();
      document.documentElement.classList.add('js-modal--open');
    }
  };

  const closeModal = (modal) => {
    if (modal instanceof HTMLDialogElement && modal.open) {
      modal.close();
    }
  };

  // 背面のスクロールロックは、フェードアウトの transitionend を待ってから解除する。
  // transitionend は opacity 以外(display/overlay など)でも発火するため
  // プロパティ名を判定し、さらに何らかの理由で opacity の transitionend が
  // 発火しないケース(開いた直後に即座に閉じた等、値が実質変化しない場合)
  // に備えてタイムアウトでもフォールバックする。
  const handleModalClose = (modal) => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      document.documentElement.classList.remove('js-modal--open');
      return;
    }

    const finish = () => {
      document.documentElement.classList.remove('js-modal--open');
      modal.removeEventListener('transitionend', handleTransitionEnd);
      clearTimeout(fallbackTimer);
    };

    const handleTransitionEnd = (event) => {
      if (event.propertyName === 'opacity') finish();
    };

    modal.addEventListener('transitionend', handleTransitionEnd);
    const fallbackTimer = setTimeout(finish, CLOSE_FALLBACK_MS);
  };

  // dialog の close イベントはバブリングしないため、委譲では拾えない。
  // 各 dialog に直接リスナーを張る必要があるので、新しく追加された
  // dialog も MutationObserver で検知してここを通す。
  const boundModals = new WeakSet();
  const bindModal = (modal) => {
    if (boundModals.has(modal)) return;
    boundModals.add(modal);

    // dialog は showModal() で「トップレイヤー」に昇格し、祖先の
    // overflow/transform/z-index の影響を受けずに最前面へ表示される。
    // ただし祖先に display:none があると、トップレイヤーであっても
    // 描画されない(サイズ0x0で不可視になる)という仕様上の制約がある。
    // js-tab--content のような「非アクティブ時に display:none にする」
    // 要素の中にモーダルを置くケースがこれに該当するため、初期化時に
    // dialog を body 直下へ退避させ、この問題を根本的に回避する。
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    modal.addEventListener('close', () => handleModalClose(modal));
  };

  // span 等のネイティブに操作できない要素にキーボード操作を持たせる。
  // button / a はネイティブ挙動に任せるため対象外。
  const enhancedTriggers = new WeakSet();
  const enhanceTrigger = (trigger) => {
    if (enhancedTriggers.has(trigger)) return;
    enhancedTriggers.add(trigger);

    if (trigger.tagName === 'BUTTON' || trigger.tagName === 'A') return;
    if (!trigger.hasAttribute('role')) trigger.setAttribute('role', 'button');
    if (!trigger.hasAttribute('tabindex')) trigger.setAttribute('tabindex', '0');
  };

  const scan = (root) => {
    root.querySelectorAll(MODAL_SELECTOR).forEach(bindModal);
    root.querySelectorAll(TRIGGER_SELECTOR).forEach(enhanceTrigger);

    if (root instanceof Element) {
      if (root.matches(MODAL_SELECTOR)) bindModal(root);
      if (root.matches(TRIGGER_SELECTOR)) enhanceTrigger(root);
    }
  };

  document.addEventListener('DOMContentLoaded', () => scan(document));

  // li の動的追加など、後から挿入された .js-modal--trigger / .js-modal--content にも対応する
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // クリックはすべて委譲で処理するため、後から追加された要素にも自動で効く
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      const targetId = trigger.dataset.modalTarget;
      const modal = targetId && document.getElementById(targetId);
      openModal(modal);
      return;
    }

    const closeButton = event.target.closest(CLOSE_SELECTOR);
    if (closeButton) {
      closeModal(closeButton.closest(MODAL_SELECTOR));
      return;
    }

    // dialog 自身(背景/backdrop 部分)がクリックされた場合のみ閉じる
    if (event.target.matches(MODAL_SELECTOR)) {
      closeModal(event.target);
    }
  });

  // Enter / Space でトリガーを操作できるようにする(button, a は対象外)
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger || trigger.tagName === 'BUTTON' || trigger.tagName === 'A') return;

    event.preventDefault();
    const targetId = trigger.dataset.modalTarget;
    const modal = targetId && document.getElementById(targetId);
    openModal(modal);
  });

  // showBallon() など、トリガー要素を介さず JS から直接開閉したい場合に使う
  window.jsModal = { open: openModal, close: closeModal };
})();

/* ==========================================================================
   タブ (.js-tab--trigger / .js-tab--content)
   モーダルとは完全に独立した IIFE。変数もセレクタも共有しないため、
   どちらか一方だけを使うページに片方のコードだけ残しても問題なく動く。
   ========================================================================== */
(() => {
  const TRIGGER_SELECTOR = '.js-tab--trigger';
  const CONTENT_SELECTOR = '.js-tab--content';
  const CONTENT_GROUP_SELECTOR = '.js-tab--content-group';
  const DEFAULT_CONTENT_ID = 'js-tab--content--default';
  const ACTIVE_CLASS = 'is-active';

  // .js-tab--content-group の上端(ドキュメント座標)までスクロールする。
  // 固定メニュー機能が window.jsFixedMenu を公開しているページでは、その高さ分
  // だけ上の位置までスクロールする(固定表示されたメニューに上端が隠れないように)。
  // 公開されていないページでは高さ0として扱い、そのまま上端までスクロールする。
  const scrollToContentGroupTop = () => {
    const contentGroup = document.querySelector(CONTENT_GROUP_SELECTOR);
    if (!contentGroup) return;

    const fixedMenuHeight = window.jsFixedMenu ? window.jsFixedMenu.getHeight() : 0;
    const top = contentGroup.getBoundingClientRect().top + window.scrollY - fixedMenuHeight;
    window.scrollTo({ top, left: 0 });
  };

  // scroll が true のときだけ .js-tab--content-group の上端までスクロールする
  // (初期表示時のデフォルトタブ活性化ではスクロールさせたくないため呼び分ける)
  const activateTab = (trigger, { scroll = false } = {}) => {
    const targetId = trigger.dataset.tabTarget;
    const target = targetId && document.getElementById(targetId);
    if (!(target instanceof HTMLElement) || !target.matches(CONTENT_SELECTOR)) return;

    document.querySelectorAll(TRIGGER_SELECTOR).forEach((t) => {
      const isActive = t === trigger;
      t.classList.toggle(ACTIVE_CLASS, isActive);
      t.setAttribute('aria-selected', String(isActive));
    });

    document.querySelectorAll(CONTENT_SELECTOR).forEach((content) => {
      content.classList.toggle(ACTIVE_CLASS, content === target);
    });

    if (scroll) scrollToContentGroupTop();
  };

  // span 等のネイティブに操作できない要素にキーボード操作を持たせる。
  const enhancedTriggers = new WeakSet();
  const enhanceTrigger = (trigger) => {
    if (enhancedTriggers.has(trigger)) return;
    enhancedTriggers.add(trigger);

    if (trigger.tagName === 'BUTTON' || trigger.tagName === 'A') {
      if (!trigger.hasAttribute('role')) trigger.setAttribute('role', 'tab');
      return;
    }
    if (!trigger.hasAttribute('role')) trigger.setAttribute('role', 'tab');
    if (!trigger.hasAttribute('tabindex')) trigger.setAttribute('tabindex', '0');
  };

  const scan = (root) => {
    root.querySelectorAll(TRIGGER_SELECTOR).forEach(enhanceTrigger);
    if (root instanceof Element && root.matches(TRIGGER_SELECTOR)) enhanceTrigger(root);
  };

  // 初期表示時、どのタブもアクティブでなければ既定のタブを開いておく。
  // #js-tab--content--default があればそれを優先して表示する(この場合、
  // どのトリガーにも対応しない表示のためtriggerにis-activeは付与しない)。
  // 無ければ従来通り先頭のトリガーを開く。
  const activateDefaultTab = () => {
    if (document.querySelector(`${CONTENT_SELECTOR}.${ACTIVE_CLASS}`)) return;

    const defaultContent = document.getElementById(DEFAULT_CONTENT_ID);
    if (defaultContent instanceof HTMLElement && defaultContent.matches(CONTENT_SELECTOR)) {
      document.querySelectorAll(CONTENT_SELECTOR).forEach((content) => {
        content.classList.toggle(ACTIVE_CLASS, content === defaultContent);
      });
      return;
    }

    const firstTrigger = document.querySelector(TRIGGER_SELECTOR);
    if (firstTrigger) activateTab(firstTrigger);
  };

  document.addEventListener('DOMContentLoaded', () => {
    scan(document);
    activateDefaultTab();
  });

  // トリガー/コンテンツが後から動的に追加されるケースにも対応する
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (trigger) activateTab(trigger, { scroll: false });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger || trigger.tagName === 'BUTTON' || trigger.tagName === 'A') return;

    event.preventDefault();
    activateTab(trigger, { scroll: false });
  });
})();

  /* ==========================================================================
     スクロールでのis-active付与 (.js-addClass01 / .js-addClass02)
     ページ内の他の機能とは独立したIIFE。
     ========================================================================== */
  (() => {
    const TARGET_SELECTOR = ".js-addClass01";
    const NEXT_CLASS = "js-addClass02";
    const ACTIVE_CLASS = "is-active";
    const NEXT_DELAY_MS = 1200; // js-addClass01の直後の兄弟js-addClass02にis-activeを付けるまでの遅延
    const CHILD_DELAY_MS = 500; // js-addClass01の子孫js-addClass02にis-activeを付けるまでの遅延

    const targets = Array.from(document.querySelectorAll(TARGET_SELECTOR));
    if (targets.length === 0) return;

    // js-addClass01にis-activeを付与し、
    // - 直後の兄弟がjs-addClass02なら1.2秒遅らせてそちらにもis-activeを付与する
    // - 要素内(子孫)にjs-addClass02があれば0.5秒遅らせてそちらにもis-activeを付与する
    const activate = (el) => {
      el.classList.add(ACTIVE_CLASS);

      const next = el.nextElementSibling;
      if (next && next.classList.contains(NEXT_CLASS)) {
        setTimeout(() => next.classList.add(ACTIVE_CLASS), NEXT_DELAY_MS);
      }

      const children = el.querySelectorAll(`.${NEXT_CLASS}`);
      children.forEach((child) => {
        setTimeout(() => child.classList.add(ACTIVE_CLASS), CHILD_DELAY_MS);
      });
    };

    // 各js-addClass01の上端がウィンドウの高さの1/3のラインまで
    // スクロールしてきたらis-activeを付与する(一度付いたら戻さない)
    const checkTargets = () => {
      const triggerLine = window.innerHeight / 4 * 3;

      targets.forEach((el) => {
        if (el.classList.contains(ACTIVE_CLASS)) return;
        if (el.getBoundingClientRect().top <= triggerLine) activate(el);
      });
    };

    window.addEventListener("scroll", checkTargets, { passive: false });
    window.addEventListener("resize", checkTargets);
    checkTargets(); // 初期表示時点で既に条件を満たしている要素があれば即時反映する
  })();

/* ==========================================================================
   フッター手前での #mainContainer::before / ::after の固定解除
   #mainContainer::before は position:fixed; bottom(またはtop):var(--footer-overlap)、
   #mainContainer::after は position:fixed; top:50% + transform: translate(x, calc(-50% - var(--footer-overlap)))
   で上下中央に固定されている装飾のため、そのままだとスクロールで
   #shopify-section-footer に重なってしまう。
   ビューポート下端が #shopify-section-footer の上端を超えた分だけ
   --footer-overlap を大きくすることで、::before はフッター上端でせり上がって
   止まったように、::after は中央固定位置からその分だけ上にずれるように見せる
   (どちらも同じ --footer-overlap の値を使い回している)。
   モーダル/タブとは完全に独立したIIFE。
   ========================================================================== */
(() => {
  const container = document.getElementById('shopify-section-template--27293033759039__content_ouen_color');
  const footer = document.getElementById('shopify-section-footer');
  if (!container || !footer) return;

  const updateFooterOverlap = () => {
    const footerTop = footer.getBoundingClientRect().top + window.scrollY;
    const viewportBottom = window.scrollY + window.innerHeight;

    // ビューポート下端がフッター上端より下に来た分だけbottomを持ち上げる
    // (フッターに到達していない間は0のまま、従来通りbottom:0の固定表示)
    const overlap = Math.max(0, viewportBottom - footerTop) - 5;

    container.style.setProperty('--footer-overlap', `${overlap}px`);
  };

  window.addEventListener('scroll', updateFooterOverlap, { passive: true });
  window.addEventListener('resize', updateFooterOverlap);
  updateFooterOverlap(); // 初期表示時点で既に条件を満たしていれば即時反映する
})();
